import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import type { ScenarioDto, SynthesizeSpeechCommand } from '@/api-gen/api.schemas';
import { PersonaUsage } from '@/api-gen/api.schemas';
import { speechSynthesizeSpeech } from '@/api-gen/api.ts';

import AudioActions from '../../../actions/audio/audioAction.ts';
import type { AxiosResponse } from 'axios';
import * as DialogProcessorSelector from 'app/selectors/dialogProcessorSelector.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';

import VoiceRecognitionStatus from 'app/models/voiceRecognition/VoiceRecognitionStatus.ts';
import { VoiceRecognitionActions } from 'app/actions/voiceRecognition/voiceRecognitionActions.ts';
import type { IDProcessState } from 'app/models/dialogs/dialogProcess.ts';
import { LinkedScenario } from 'app/models/intents/intentNodeProperties.ts';
import logger from 'app/utils/logger.ts';

function* getAudio() {
  // currentChartScenario reads the scenarios list entry, which getDetailedScenario enriches
  // with the full DTO (scenarioPersonas, ttsProvider, speed) — unlike the rootScenario
  // snapshot taken by initToolBar before the detailed load finishes.
  const scenario: ScenarioDto = yield select(ScenarioSelectors.currentChartScenario);
  const dialogProcessorData: IDProcessState = yield select(DialogProcessorSelector.dialogProcessorSelector);

  try {
    const currentBlock = dialogProcessorData.blocks.currentBlock;
    const text = currentBlock?.text;

    if (!text) {
      throw new Error('No AI response for Text to Speech!');
    }

    // Prefer block persona voice; fall back to scenario default
    const blockPersona = currentBlock?.scenarioPersona?.persona;
    const defaultPersona = scenario?.scenarioPersonas?.find((sp) =>
      sp.personaUsages?.includes(PersonaUsage.Default)
    )?.persona;
    const persona = blockPersona ?? defaultPersona;

    const payload: SynthesizeSpeechCommand = {
      language: scenario.langCode,
      sex: persona?.gender,
      service: persona?.ttsProvider ?? scenario.ttsProvider,
      voiceName: persona?.voiceName,
      voiceId: persona?.voiceId,
      text: text,
      speed: scenario.speed,
      intentId: currentBlock.id,
      // Let the backend resolve the block/default persona voice (OverrideVoiceConfig);
      // model-led turns have no block, so intentId alone can't identify the scenario.
      scenarioId: scenario.id,
      scenarioVersionId: scenario.versionId
    };

    const response: AxiosResponse<string> = yield call(
      // Orval client uses body object directly
      speechSynthesizeSpeech,
      payload as any
    );

    yield put(AudioActions.setAudioAIResponse(response.data));
    yield put(VoiceRecognitionActions.setRecognitionStatus(VoiceRecognitionStatus.AudioIsPlaying));
  } catch (ex) {
    logger.error('Failed to synthesize AI response audio', ex);
    yield put(VoiceRecognitionActions.setRecognitionStatus(VoiceRecognitionStatus.AudioWasEnded));
  }
}

function* watchGetAudio() {
  yield takeEvery(AudioActions.Type.GET_AUDIO, getAudio);
}

export default function* watchAllAudiosSaga() {
  yield fork(watchGetAudio);
}
